import { useEffect, useMemo } from 'react'
import {
  type MotionValue,
  motionValue,
  type SpringOptions,
  useMotionValue,
  useSpring,
} from 'motion/react'
import { followSpring } from '@/animation/transitions'

/**
 * Used by components that accept either a motion value or a plain number.
 *
 * @param value the motion value to use, if it's defined. A plain number is
 * converted to a motion value.
 * @param defaultValue the value when none is given. Must be stable between
 * renders.
 */
export function useFallbackMotionValue(
  value: MotionValue<number> | number | undefined,
  defaultValue = 0,
): MotionValue<number> {
  const staticValue = typeof value === 'number' ? value : defaultValue
  const fallback = useMemo(() => motionValue(staticValue), [staticValue])
  return typeof value === 'object' ? value : fallback
}

/**
 * Springs towards the value whenever it changes.
 *
 * @param value
 * @param from the value to spring from on mount. Defaults to `value`.
 * @param options
 */
export function useSpringValue(
  value: number,
  from = value,
  options: SpringOptions = followSpring,
): MotionValue<number> {
  const target = useMotionValue(from)
  useEffect(() => {
    target.set(value)
  }, [target, value])
  return useSpring(target, options)
}
